<script lang="ts">
	import { devices_settings } from 'src/stores/device-runtime/status';
	//Stores
	import { req_locked_devices } from 'src/stores/device-runtime/local-auth';
	import {
		getDeviceStatus,
		getDeviceSettings,
		readDeviceSettings,
	} from 'src/stores/device-runtime/status-selectors';
	import { real_id } from '../../../../helpers/interface_helpers';

	//Types
	import type { Device } from '../../../../types/Device';

	//Helpers
	import { scheduleDataFormatReceive } from '../SchedulesHelpers/schedules';

	//Svelte hooks
	import { beforeUpdate, onMount } from 'svelte';

	//Services
	import { postReq } from '../../../../services/http';
	import { format } from 'svelte-i18n';

	//Design system components
	import { Button, Container, Loader, Text, toast } from '@Shelly/shelly-design-system';

	//Helpers
	import {
		parseStatisticsDataTrv,
		prepareScheduleData,
		validateSchedules,
	} from '../../../../helpers/trv_helpers';
	import { onSettingsResponse } from '../../../../helpers/device_settings_helpers';

	//Components
	import SchedulesList from '../SchedulesHelpers/SchedulesList.svelte';
	import SchedulesListV2 from '../SchedulesHelpers/SchedulesListV2.svelte';
	import ProfileSelector from './ProfileSelector.svelte';
	import ActionTemperature from './Components/ActionTemperature.svelte';
	import DayPicker from './Components/DayPicker.svelte';
	import TimePicker from './Components/TimePicker.svelte';

	import TrvSchedulesChart from 'src/components/Chart/ChartJS/TRVSchedulesChart.svelte';
	import WizzardWrapperTrvSchedules from 'src/components/ModalWizzard/WizzardWrapperTrvSchedules.svelte';

	export let device: Device;
	export let useNewLayout = false;

	let settings = readDeviceSettings(device.id);
	$: settingsStore = getDeviceSettings(device.id);
	$: settings = $settingsStore ?? settings;

	let checked = settings?.thermostats[device.channel]?.schedule,
		profileOptions = settings?.thermostats[device.channel]?.schedule_profile_names,
		unit = settings?.thermostats[device.channel].target_t.units,
		showModal = false,
		scheduleRules = settings?.thermostats[device.channel]?.schedule_rules.map((x) => (x = x)), //prevent store array mutation
		chartData: {
			intervals: any[];
			current: any;
			units: any;
			type: string;
			base: string;
			height: string;
		},
		loadingProfileChange = false,
		profileRename = false,
		loadingList = false,
		isStoreUpdated = false,
		formattedRules: string[],
		selectedProfile: number = checked ? settings?.thermostats[device.channel]?.schedule_profile : 0,
		options = ['open', 'close', 'temperature'],
		_options = [],
		timeStamp;

	options.forEach((prop) => {
		_options.push($format(`schedule_action_${prop}`));
	});

	let cachedRules = [...scheduleRules], //prevent scheduleRules mutation
		reqLocked = false;

	if (!profileOptions.includes($format('disable'))) {
		profileOptions.unshift($format('disable'));
	}
	let profileName: string = profileOptions[selectedProfile];
	const defaultSchedule = {
		brightness: `${settings?.thermostats[0]?.target_t?.value}`,
		selection: undefined,
		days: [0, 1, 2, 3, 4, 5, 6],
		sun: undefined,
		time: '00:00',
		type: 'Time',
		unit,
		index: undefined,
		min: unit === 'C' ? 4 : 40,
		max: unit === 'C' ? 31 : 87,
		_options,
		options,
		edit: undefined,
	};

	let editableObject = defaultSchedule;

	const updateUIList = (rules) => {
		formattedRules = scheduleDataFormatReceive(rules, '', device, unit);
	};
	const updateData = () => {
		settings = $settingsStore;
		checked = settings?.thermostats[device.channel]?.schedule;

		selectedProfile = checked ? settings?.thermostats[device.channel]?.schedule_profile : 0;
		profileName = profileOptions[selectedProfile];
		cachedRules = scheduleRules;
		scheduleRules = settings?.thermostats[device.channel]?.schedule_rules.map((x) => x);

		if (scheduleRules.length > 0) {
			updateUIList(scheduleRules);
		} else {
			formattedRules = [];
		}
	};

	const updateChartData = () => {
		chartData = parseStatisticsDataTrv(
			device,
			settings,
			formattedRules,
			editableObject.min,
			editableObject.max,
		);
	};

	const resetLoaders = () => {
		loadingList = false;
		profileRename = false;
		loadingProfileChange = false;
		isStoreUpdated = false;
		timeStamp = undefined;
		req_locked_devices.remove(device.id);
	};

	const compareChanges = () => {
		let change = false;
		if (loadingProfileChange) {
			if (
				settings?.thermostats?.[device.channel]?.schedule_profile !==
					$settingsStore?.thermostats?.[device.channel]?.schedule_profile &&
				profileName !==
					$settingsStore?.thermostats[device.channel]?.schedule_profile_names[selectedProfile]
			) {
				change = true;
			}
			if (
				profileRename &&
				profileName !==
					$settingsStore?.thermostats[device.channel]?.schedule_profile_names[selectedProfile]
			) {
				change = true;
			}
		}
		if (loadingList) {
			if (
				settings?.thermostats[device.channel]?.schedule_rules.toString() !==
				$settingsStore?.thermostats[device.channel]?.schedule_rules.toString()
			) {
				change = true;
			}
		}
		if (isStoreUpdated) {
			loadingList = true;
			change = true;
			toast.open({
				type: 'info',
				title: $format('trv_settings_failed_try_again', {
					default: "Setting failed. Please press device's button 3 times and try again",
				}),
			});
		}

		if (change) {
			change = false;
			updateData();
			updateChartData();
			resetLoaders();
		}
	};

	onMount(() => {
		updateData();
		updateChartData();
	});

	beforeUpdate(async () => {
		reqLocked = $req_locked_devices.indexOf(device?.id) !== -1;
		if (loadingProfileChange || loadingList) {
			compareChanges();
		}
		if (
			settings?.thermostats[device.channel]?.schedule_rules.toString() !==
				$settingsStore?.thermostats[device.channel]?.schedule_rules.toString() ||
			settings?.thermostats[device.channel]?.schedule !==
				$settingsStore?.thermostats[device.channel]?.schedule
		) {
			//this check is to handle outside changes
			isStoreUpdated = true;
			compareChanges();
		}
		if (typeof timeStamp !== 'undefined') {
			const time = +(new Date().getTime() / 1000).toFixed(0);
			if (time - Number(timeStamp) > 60) {
				compareChanges();
			}
		}
	});

	const handleSave = async (data) => {
		if (validateSchedules(data.split(','))) {
			req_locked_devices.add(device.id);
			editableObject.edit = false;
			timeStamp = +(new Date().getTime() / 1000).toFixed(0);

			const res = await postReq(
				`device/generic_command`,
				{
					id: device.id,
					command: 'sensor:schedule',
					payload: JSON.stringify({
						id: device.channel,
						enabled: 1,
						schedule_rules: data,
						profile: selectedProfile,
						profile_name: profileOptions[selectedProfile],
					}),
				},
				40000,
			);
			let resp = onSettingsResponse(res, device);
			updateUIList(data.split(','));
			if (!resp) {
				scheduleRules = [...cachedRules];
				updateUIList(scheduleRules);
				loadingList = false;
				req_locked_devices.remove(device.id);
			} else {
				timeStamp = undefined;
				loadingList = true;
				// devices_settings.update_devices_settings(device.id, settings);//will not override settings so we will wait for store update
				compareChanges();
			}
			editableObject = defaultSchedule;
		} else {
			toast.open({
				type: 'info',
				title: $format('schedules_time_duplication', {
					default: "Schedules different days can't have equal times.",
				}),
			});
			scheduleRules = cachedRules;
			updateUIList(scheduleRules);
		}
	};

	const handleEdit = (e) => {
		let index = e.detail.index;
		editableObject = {
			...editableObject,
			...formattedRules[index],
			edit: true,
			index,
		};
		showModal = true;
	};

	const handleDelete = (e) => {
		let index = e.detail;
		const _scheduleRules = [...scheduleRules]; //prevent mutation

		if (_scheduleRules.length > 0) {
			_scheduleRules.splice(index, 1);
			updateUIList(_scheduleRules);
			handleSave(_scheduleRules.join(','));
		} else {
			handleSave('');
		}
	};

	const handleAdd = () => {
		const defaultSchedule = {
			brightness: `${settings?.thermostats[0]?.target_t?.value}`,
			selection: undefined,
			days: [0, 1, 2, 3, 4, 5, 6],
			sun: undefined,
			time: '00:00',
			type: 'Time',
			unit,
		};
		editableObject = {
			...editableObject,
			...defaultSchedule,
		};

		if (formattedRules?.length === 20) {
			return toast.open({
				type: 'info',
				title: $format('scheds_limit_trv_toast', {
					default: `Can't add more than 20 profiles per profile!`,
				}),
			});
		}
		showModal = true;
	};

	const handleProfileChange = async (e) => {
		const _selectedProfile = e.detail.selectedProfile;
		let res;

		profileName = profileOptions[_selectedProfile];
		loadingProfileChange = true;
		req_locked_devices.add(device.id);

		if (_selectedProfile === 0) {
			checked = false;
			res = await postReq(
				`device/generic_command`,
				{
					id: device.id,
					command: 'sensor:schedule',
					payload: JSON.stringify({
						id: device.channel,
						enabled: 0,
					}),
				},
				40000,
			);
			selectedProfile = 0;
			scheduleRules = [];
			loadingProfileChange = false;
			return;
		} else {
			if (!checked) {
				checked = true;
				let enabler = await postReq(
					`device/generic_command`,
					{
						id: device.id,
						command: 'sensor:schedule',
						payload: JSON.stringify({
							id: device.channel,
							enabled: 1,
						}),
					},
					40000,
				);
				if (enabler.isok) {
				} else {
					toast.open({ type: 'danger', title: $format('error_global') });
				}
			}
		}
		if (checked) {
			res = await postReq(
				`device/generic_command`,
				{
					id: device.id,
					command: 'sensor:schedule_profile',
					payload: JSON.stringify({
						id: device.channel,
						profile: _selectedProfile,
						profile_name: profileOptions[_selectedProfile],
					}),
				},
				40000,
			);
		}
		if (res.isok) {
			selectedProfile = _selectedProfile;
			profileName = profileOptions[selectedProfile];
			toast.open({
				type: 'success',
				title: $format('sched_profile_change_with_values', {
					values: {
						device_name: device.name,
						name: profileOptions[_selectedProfile],
						default: `Schedule profile was changed to ${profileOptions[_selectedProfile]}.`,
					},
				}),
			});
		} else {
			profileName = profileOptions[selectedProfile];
			toast.open({ type: 'danger', title: $format('error_global') });
			req_locked_devices.remove(device.id);
		}
		// loadingList = true;
	};

	const handleChangeName = (e) => {
		profileOptions = e.detail.profileOptions;
		profileName = e.detail.profileName;
		profileRename = true;
		handleProfileChange({
			detail: { selectedProfile: profileOptions.indexOf(profileName) },
		});
	};

	const wizardItems = [
		{
			active: true,
			activeIndex: 0,
			disabled: false,
			showMoreDots: false,
			stepNumber: 1,
			title: $format('schedules_wizard_items_week_days', {
				default: 'Week days',
			}),
			tooltipTitle: $format('choose_weekday'),
			component: DayPicker,
		},
		{
			active: false,
			activeIndex: 1,
			disabled: false,
			showMoreDots: false,
			stepNumber: 2,
			title: $format('schedules_wizard_items_time', { default: 'Time' }),
			component: TimePicker,
			tooltipTitle: $format('choose_time'),
		},
		{
			active: false,
			activeIndex: 2,
			disabled: false,
			showMoreDots: false,
			stepNumber: 3,
			title: $format('schedules_wizard_items_action', { default: 'Action' }),
			component: ActionTemperature,
			tooltipTitle: $format('select_type', {
				values: { type: $format('process_action') },
			}),
		},
	];
</script>

<ProfileSelector
	{profileName}
	{profileOptions}
	{useNewLayout}
	disabled={!checked || reqLocked || loadingList}
	on:profileChange={handleProfileChange}
	on:changedName={handleChangeName}
/>
{#if loadingProfileChange}
	<div class="d-flex justify-content-center">
		<Loader />
	</div>
{:else if checked}
	{#if Array.isArray(formattedRules)}
		{#if typeof chartData !== 'undefined' && chartData?.intervals?.length > 0}
			<Container
				type="modalContainer"
				class="mb-s"
				color={useNewLayout ? '--color-0' : ''}
				noScroll={true}
			>
				{#if reqLocked}
					<div class="d-flex justify-content-center">
						<Loader />
					</div>
				{:else}
					<TrvSchedulesChart data={chartData} />
				{/if}
			</Container>
		{/if}

		{#if useNewLayout}
			<SchedulesListV2
				on:edit={handleEdit}
				on:delete={handleDelete}
				{device}
				schedule_rules={formattedRules}
				{checked}
				disabled={loadingList || reqLocked}
			/>
		{:else}
			<SchedulesList
				on:edit={handleEdit}
				on:delete={handleDelete}
				{device}
				schedule_rules={formattedRules}
				{checked}
				disabled={loadingList || reqLocked}
			/>
		{/if}
	{/if}
	<Button disabled={reqLocked} class="mt-m mb-s" width="full" size="m" on:click={handleAdd}
		>{$format('button_add')}</Button
	>
{/if}

{#if showModal}
	<WizzardWrapperTrvSchedules
		wizardContentClasses="pl-0 pr-0"
		bind:showModal
		title={$format('weekly_schedules')}
		{wizardItems}
		callback={() =>
			handleSave(
				prepareScheduleData(
					scheduleDataFormatReceive(scheduleRules, '', device, unit),
					editableObject,
				),
			)}
		bind:editableObject
	/>
{/if}
